import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../tenant/auth.service';
import { SessionService } from '../tenant/session.service';
import { Capacidad, PerfilEnSesion, PerfilVertical, RespuestaDePerfil, Termino } from './perfil.model';
import { EstadoDelInventario, InventarioArrancandoError, conReintentoAlDespertar } from '../red/reintento-al-despertar';

/** Misma familia que `suresell.session` y `suresell.jwt`; aparte, atado al negocio. */
const STORAGE_KEY = 'suresell.perfil';

/**
 * El único término con valor propio cuando el perfil no lo define: `vendedor` (F1.14). V404 hace
 * que el restaurante diga «mesero»; los demás oficios no lo definen y dicen «vendedor». Sin esto
 * el plural caería a la clave («vendedor»). El oficio lo dice el perfil: aquí no va ninguno más.
 */
const POR_DEFECTO: Readonly<Record<string, Termino>> = { vendedor: { singular: 'vendedor', plural: 'vendedores' } };

/** Formas de un término: `p` plural, `t` con inicial mayúscula. Se combinan: `pt`. */
export type FormaDeTermino = '' | 'p' | 't' | 'pt' | 'tp';

/**
 * El perfil vertical del negocio en sesión: con qué color se pinta el POS, qué
 * capacidades tiene y cómo se le habla.
 *
 * <h2>Una petición por sesión, no una por pantalla</h2>
 *
 * Vive en `ms-smart-inventory` (`inventario.perfil_asignado`, V50). El login lo
 * hace `ms-order-product` y no sabe de perfiles, así que este servicio lo pide
 * <b>una vez por sesión</b>: un `effect` mira el `tenantId` de la sesión y,
 * cuando cambia (login, logout, F5), carga el perfil y lo guarda en
 * `localStorage` junto al negocio. Un F5 pinta con lo guardado sin esperar la
 * red y refresca detrás. Ninguna pantalla llama a nada: leen las señales de
 * aquí. Es el mismo diseño que dejó D en el panel (2026-09-03).
 *
 * <h2>Sin sesión real no se pide nada</h2>
 *
 * Sin JWT no hay a quién preguntar: se limpia lo guardado y no sale ninguna
 * petición, que de todas formas volvería 401.
 *
 * <h2>Sin perfil no se inventa uno</h2>
 *
 * Sin perfil el POS se ve exactamente como hoy (los tokens de `styles.css`
 * tienen su valor de reserva). Inventar «restaurante» escondería que falta un
 * paso administrativo.
 *
 * <h2>🔴 Añadir una vertical no puede requerir tocar código</h2>
 *
 * Este servicio no conoce ningún perfil por nombre. Si alguna vez hace falta
 * un `if (perfil.codigo === '...')`, el diseño se rompió.
 */
@Injectable({ providedIn: 'root' })
export class PerfilService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(SessionService);
  private readonly auth = inject(AuthService);
  private readonly base = PerfilService.direccion((environment as { inventoryApiUrl?: string }).inventoryApiUrl);

  private readonly _estado = signal<PerfilEnSesion | null>(this.restaurar());

  /** El perfil vigente, o `null` si no hay (todavía) uno. */
  readonly perfil = computed<PerfilVertical | null>(() => this._estado()?.perfil ?? null);
  readonly asignado = computed(() => this.perfil() !== null);
  /** Por qué no hay perfil: sin asignar, o el inventario no está configurado en este despliegue. */
  readonly motivoSinPerfil = computed(() => this._estado()?.motivo ?? null);
  readonly cargando = signal(false);
  private readonly inventario = inject(EstadoDelInventario);

  constructor() {
    // Una vez por sesión: cuando cambia el negocio, no cuando cambia la pantalla.
    effect(() => {
      const tenantId = this.session.session()?.tenantId ?? null;
      untracked(() => void this.sincronizar(tenantId));
    });

    // El tema se aplica al documento: los componentes no preguntan por colores,
    // leen tokens (`--color-accent`), igual que en el panel.
    effect(() => this.aplicarTema(this.perfil()));
  }

  // =====================================================================
  // Capacidades y terminología
  // =====================================================================

  /**
   * ¿El perfil trae esta capacidad? Sin perfil responde `true`: no se deja a
   * nadie sin trabajar por un paso administrativo.
   */
  tiene(capacidad: Capacidad): boolean {
    const p = this.perfil();
    return p === null || p.capacidades.includes(capacidad);
  }

  /** El término del perfil para una clave; si no lo define, el de `POR_DEFECTO` o la clave misma. */
  termino(clave: string, forma: FormaDeTermino = ''): string {
    const t: Termino = this.perfil()?.terminologia?.[clave] ?? POR_DEFECTO[clave] ?? { singular: clave, plural: clave };
    const palabra = forma.includes('p') ? t.plural : t.singular;
    return forma.includes('t') ? PerfilService.conInicial(palabra) : palabra;
  }

  // =====================================================================
  // Carga
  // =====================================================================

  /** Vuelve a pedir el perfil al servicio. */
  async recargar(): Promise<void> {
    const tenantId = this.session.session()?.tenantId;
    if (tenantId) {
      await this.cargar(tenantId);
    }
  }

  private async sincronizar(tenantId: string | null): Promise<void> {
    // Sin negocio o sin token no hay a quién preguntar.
    if (!tenantId || !this.auth.getToken()) {
      this.guardar(null);
      return;
    }
    // Lo guardado de OTRO negocio no sirve: se limpia antes de pedir.
    if (this._estado()?.tenantId !== tenantId) {
      this.guardar(null);
    }
    await this.cargar(tenantId);
  }

  private async cargar(tenantId: string): Promise<void> {
    if (!this.base) {
      // No es un error: es un despliegue sin inventario.
      this.guardar({ tenantId, perfil: null, motivo: 'SIN_SERVICIO', cargadoEn: new Date().toISOString() });
      return;
    }
    this.cargando.set(true);
    try {
      // Al abrir la caja el inventario puede estar dormido (504 del borde): se reintenta sin avisar.
      const r = await conReintentoAlDespertar(() =>
        firstValueFrom(this.http.get<RespuestaDePerfil>(`${this.base}/perfil`)));
      this.inventario.listo();
      if (r.asignado) {
        const { asignado, ...perfil } = r;
        this.guardar({ tenantId, perfil, motivo: null, cargadoEn: new Date().toISOString() });
      } else {
        this.guardar({ tenantId, perfil: null, motivo: r.motivo, cargadoEn: new Date().toISOString() });
      }
    } catch (e) {
      if (e instanceof InventarioArrancandoError) this.inventario.arrancando();
      // Se conserva lo guardado (si lo hay): el POS trabaja sin red y un fallo
      // de red no debe cambiar el color de la pantalla a media jornada.
    } finally {
      this.cargando.set(false);
    }
  }

  private guardar(estado: PerfilEnSesion | null): void {
    this._estado.set(estado);
    try {
      if (estado) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(estado));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      /* solo en memoria */
    }
  }

  private restaurar(): PerfilEnSesion | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as PerfilEnSesion) : null;
    } catch {
      return null;
    }
  }

  // =====================================================================
  // Tema
  // =====================================================================

  /**
   * Escribe el tema del perfil como variables CSS en `<html>`. `styles.css`
   * las lee con un valor de reserva (`var(--perfil-acento, #a88a6d)`), así que
   * sin perfil el POS se ve exactamente como hoy.
   *
   * 🔴 Es el ÚNICO sitio donde un color del perfil toca el DOM. Un componente
   * que escriba `perfil.tema.acento` en un `[style]` está mal: lee el token.
   */
  private aplicarTema(perfil: PerfilVertical | null): void {
      const raiz = document.documentElement;
      // Medido en staging (2026-09-08): con `transition: all`, cualquier
      // propiedad que dependa de una variable que cambia DESPUÉS del primer
      // render se queda en el valor viejo (Chrome no completa esa
      // transición). El marcador del sidebar se quedaba en el color de
      // reserva hasta cambiar de pantalla. Mientras se aplica el tema, el
      // documento suspende las transiciones dos fotogramas (`styles`), y así
      // ningún componente tiene que saberlo.
      raiz.classList.add('perfil-cambiando');
      if (!perfil) {
          raiz.style.removeProperty('--perfil-primario');
          raiz.style.removeProperty('--perfil-acento');
          raiz.removeAttribute('data-densidad');
      } else {
          // Misiones 3.3.0 («Siguiente» con texto blanco y sin fondo): un color que no es un hexadecimal válido —un perfil
          // sin `tema`, uno guardado de antes— dejaba la variable inválida y el botón primario sin fondo. Solo se pone un
          // color válido; si no, se quita y vuelve el de reserva.
          PerfilService.ponerColor(raiz, '--perfil-primario', perfil.tema?.primario);
          PerfilService.ponerColor(raiz, '--perfil-acento', perfil.tema?.acento);
          if (perfil.tema?.densidad) raiz.setAttribute('data-densidad', perfil.tema.densidad);
          else raiz.removeAttribute('data-densidad');
      }
      // setTimeout y no requestAnimationFrame: en una pestaña en segundo plano
        // los fotogramas no corren y la clase se quedaría puesta hasta volver.
        setTimeout(() => raiz.classList.remove('perfil-cambiando'), 120);
  }

  /** `.../api/inventory`, con o sin la ruta ya puesta y sin barra final. Vacío si no hay servicio. */
  /** `#rgb` o `#rrggbb` (la base guarda seis dígitos); cualquier otra cosa no toca el documento. */
  static ponerColor(raiz: HTMLElement, propiedad: string, color: unknown): void {
    if (typeof color === 'string' && /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color.trim())) raiz.style.setProperty(propiedad, color.trim());
    else raiz.style.removeProperty(propiedad);
  }

  static direccion(url: string | null | undefined): string {
    if (!url) {
      return '';
    }
    const limpia = url.replace(/\/+$/, '');
    return limpia.endsWith('/api/inventory') ? limpia : limpia + '/api/inventory';
  }

  private static conInicial(s: string): string {
    return s.length === 0 ? s : s[0].toLocaleUpperCase('es') + s.slice(1);
  }
}
